import { Repeat } from 'lucide-react';
import { formatDuration, formatTime, type EventOccurrence } from '@awwyeah/shared';
import { ownerNames, styleForOwners, type MemberView } from '../members';
import { MemberStack } from './MemberBits';

/**
 * The workhorse agenda pill (design: Component · EventCard — 56px, member colour on the
 * left edge only, never the whole fill).
 */
export function EventPill({
  occurrence,
  members,
  onOpen,
}: {
  occurrence: EventOccurrence;
  members: ReadonlyMap<string, MemberView>;
  onOpen: (occurrence: EventOccurrence) => void;
}): JSX.Element {
  const { event } = occurrence;
  const style = styleForOwners(event.ownerIds, members);
  const names = ownerNames(event.ownerIds, members);
  const subtitle = [names, event.location].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      data-testid="event-item"
      data-event-id={event.id}
      onClick={() => onOpen(occurrence)}
      style={style.edge}
      className="flex min-h-[56px] w-full items-center gap-3 rounded-sm border border-line border-l-4 bg-surface px-3 py-2.5 text-left transition-colors duration-150 ease-app"
    >
      <span className="w-[52px] shrink-0">
        <span className="block text-sm font-semibold">
          {event.allDay ? 'All day' : formatTime(occurrence.start)}
        </span>
        {!event.allDay && (
          <span className="block text-xs text-fg-3">
            {formatDuration(occurrence.start, occurrence.end)}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base font-semibold">{event.title}</span>
        {subtitle && <span className="block truncate text-xs text-fg-3">{subtitle}</span>}
      </span>
      {event.recurrence && <Repeat className="h-3.5 w-3.5 shrink-0 text-fg-3" aria-hidden="true" />}
      <MemberStack ownerIds={event.ownerIds} byId={members} />
    </button>
  );
}
